"use client";

import React, { useMemo } from "react";
import { CustomUserType } from "../../../../../../types/profiles/authTypes";
import { Mail, Smartphone, MapPin, Lock, Cake, AtSign, CalendarCheck, IdCard, Gift } from "lucide-react";
import { isRTLText } from "../../../../../../utils/helpers/languageHelpers";
import "./PersonalDetails.css";

interface PersonalDetailsProps {
  customUser: CustomUserType;
  spiritualRebirthDay: string | null;
}

// map backend gender -> pronouns text
const getPronouns = (gender?: string | null) => {
  if (!gender) return "";
  const g = String(gender).toLowerCase();
  if (g === "male") return "He/Him";
  if (g === "female") return "She/Her";
  return "";
};

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
    : "";

const PersonalDetails: React.FC<PersonalDetailsProps> = ({ customUser, spiritualRebirthDay }) => {
  const {
    name,
    family,
    username,
    gender, // 'Male' | 'Female'
    email,
    mobile_number,
    show_email,
    show_phone_number,
    show_country,
    country_display,
    birthday,
    registration_started_at,
  } = customUser || {};

  // identity
  const displayName = useMemo(
    () => [name, family].filter(Boolean).join(" ").trim(),
    [name, family]
  );
  const pronouns = useMemo(() => getPronouns(gender), [gender]);

  // visibility
  const hasIdentity = !!displayName || !!username || !!birthday || !!pronouns;
  const hasAnyEmail = !!email;
  const hasAnyPhone = !!mobile_number;
  const hasAnyLocation = !!(country_display);
  const showRebirth = !!spiritualRebirthDay;

  const hasContact =
    (hasAnyEmail && show_email) ||
    (hasAnyPhone && show_phone_number) ||
    (hasAnyLocation && show_country) ||
    (hasAnyEmail && !show_email) ||
    (hasAnyPhone && !show_phone_number) ||
    (hasAnyLocation && !show_country);

  const joinedText = fmtDate(registration_started_at);

  return (
    <section className="pd-container" dir="ltr">
      {/* Identity block */}
      {hasIdentity && (
        <div className="pd-identity">

          {displayName ? (
            <div className="pd-name" dir={isRTLText(displayName) ? "rtl" : "ltr"}>
              <IdCard className="pd-ico" size={18} aria-hidden />
              <div>
                <span className="pd-name-text">{displayName}</span>
                {pronouns && <span className="pd-pronoun"> ({pronouns})</span>}
              </div>

            </div>
          ) : null}

          {/* username (subtle, inline) */}
          {username ? (
            <div className="pd-username" title="Username">
              <AtSign className="pd-ico" size={18} aria-hidden />
              <span className="pd-username-text">{username}</span>
            </div>
          ) : null}

          {/* birthday right under identity */}
          {birthday ? (
            <div className="pd-birthday" title="Birthday">
              <Cake className="pd-ico" size={18} aria-hidden />
              <span className="pd-birthday-text">{fmtDate(birthday)}</span>
            </div>
          ) : null}

          {showRebirth && (
            <span className="pd-rebirthday " title="Spiritual Birthday">
              <Gift className="pd-ico" size={18} aria-hidden />
              <span className="pd-rebirthday -text">{fmtDate(spiritualRebirthDay)}</span>
            </span>
          )}

          {/* Contact & location block */}
          {hasContact && (
            <div className="pd-contact">
              {/* Email */}
              {hasAnyEmail && show_email ? (
                <div className="pd-row" title="Email">
                  <Mail className="pd-ico" size={18} aria-hidden />
                  <span className="pd-text">{email}</span>
                </div>
              ) : hasAnyEmail && !show_email ? (
                <div className="pd-row" aria-label="Email hidden">
                  <Mail className="pd-ico" size={18} aria-hidden />
                  <Lock className="pd-ico-lock" size={16} aria-hidden />
                </div>
              ) : null}

              {/* Phone */}
              {hasAnyPhone && show_phone_number ? (
                <div className="pd-row" title="Phone Number">
                  <Smartphone className="pd-ico" size={18} aria-hidden />
                  <span className="pd-text">{mobile_number}</span>
                </div>
              ) : hasAnyPhone && !show_phone_number ? (
                <div className="pd-row" aria-label="Phone hidden">
                  <Smartphone className="pd-ico" size={18} aria-hidden />
                  <Lock className="pd-ico-lock" size={16} aria-hidden />
                </div>
              ) : null}

              {/* Location */}
              {hasAnyLocation && show_country ? (
                <div
                  className="pd-row"
                  title="Country"
                >
                  <MapPin className="pd-ico" size={18} aria-hidden />
                  <span className="pd-text">
                    {[country_display].filter(Boolean).join(" • ")}
                  </span>
                </div>
              ) : hasAnyLocation && !show_country ? (
                <div className="pd-row" aria-label="Location hidden">
                  <MapPin className="pd-ico" size={18} aria-hidden />
                  <Lock className="pd-ico-lock" size={16} aria-hidden />
                </div>
              ) : null}

              {/* Joined meta (clear text, no ambiguous icon) */}
              {joinedText ? (
                <div className="pd-meta" aria-label="Joined date">
                  <CalendarCheck className="pd-ico" size={18} aria-hidden />
                  <span>Joined {joinedText}</span>
                </div>
              ) : null}

            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default PersonalDetails;
